import { IReportServiceByDateResponce } from '@/domain/IReport';
import animateToggle from '@/scripts/animatetoggle';
import dropDown from '@/scripts/dropdown';
import * as XLSX from 'xlsx';
import store from '@/store';
import Axios from 'axios';
import { Vue } from 'vue-property-decorator';
import i18n from '@/i18n';
import moment from 'moment';
import MicroModal from 'micromodal';

export default {
    methods: {
        // Formatting
        formatDate(date: string): string {
            if (!date?.length) return '';
            return moment(date, 'YYYY-MM-DD').isValid() ? moment(date).format('DD.MM.YYYY') : date;
        },

        formatNumber(num: number): string {
            const parts = num.toFixed(2).split('.');
            const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            const decimalPart = parts[1];
            return integerPart + ',' + decimalPart;
        },

        formatSum(sum: number): string {
            return this.formatNumber(sum) + ' €';
        },

        totalSum(data: IReportServiceByDateResponce): number {
            // For servicebydate response
            if (Array.isArray(data?.services)) {
                return data.services.reduce((total, item) => total + item.ttlsellrate, 0);
            }
            return 0;
        },

        calculateNights(startDate: string, endDate: string): number | string {
            const start = moment(startDate).isValid() ? moment(startDate).startOf('day') : null;
            const end = moment(endDate).isValid() ? moment(endDate).startOf('day') : null;
            if (!start || !end) {
                return '';
            }
            const nights = end.diff(start, 'days');
            return nights > 0 ? nights : 0;
        },

        // For dropdown calendar
        handleDateChange() {
            // For servicebydate response
            const selectedDates = this.selectedDateRange;
            if (selectedDates.split(' to ').length === 2) {
                this.generateReportOk = true;
                this.ServiceByDateRequest.service.servicestartdate = selectedDates.split(' to ')[0];
                this.ServiceByDateRequest.service.serviceenddate = selectedDates.split(' to ')[1];
                // Format dates for the request
                this.ServiceByDateRequest.service.servicestartdate = this.ServiceByDateRequest.service.servicestartdate.replace(/\./g, '-');
                this.ServiceByDateRequest.service.serviceenddate = this.ServiceByDateRequest.service.serviceenddate.replace(/\./g, '-');

                const startParts = this.ServiceByDateRequest.service.servicestartdate.split('-');
                this.ServiceByDateRequest.service.servicestartdate = `${startParts[2]}-${startParts[1]}-${startParts[0]}`;

                const endParts = this.ServiceByDateRequest.service.serviceenddate.split('-');
                this.ServiceByDateRequest.service.serviceenddate = `${endParts[2]}-${endParts[1]}-${endParts[0]}`;

                this.selectedDateRange = this.selectedDateRange.replace('to', '—');
            }
        },
        quickSelectDates(range: string) {
            // For servicebydate response
            let startDate: moment.Moment;
            let endDate: moment.Moment;

            const now = moment();
            switch (range) {
                case 'currentMonth':
                    startDate = moment().startOf('month');
                    endDate = moment().endOf('month');
                    break;
                case 'lastMonth':
                    startDate = moment().subtract(1, 'month').startOf('month');
                    endDate = moment().subtract(1, 'month').endOf('month');
                    break;
                case 'currentYear':
                    startDate = moment().startOf('year');
                    endDate = moment().endOf('year');
                    break;
                case 'lastYear':
                    startDate = moment().subtract(1, 'year').startOf('year');
                    endDate = moment().subtract(1, 'year').endOf('year');
                    break;
                case 'yearFromNow':
                    startDate = moment();
                    endDate = moment().add(1, 'year');
                    break;
            }
            // Format the start and end dates as strings and set the selectedDateRange.
            const startDateStr = startDate.format('DD.MM.YYYY');
            const endDateStr = endDate.format('DD.MM.YYYY');

            this.selectedDateRange = `${startDateStr} to ${endDateStr}`;
            this.handleDateChange();
        },
        // animations
        dropdown(trigger) {
            dropDown(this.$refs[trigger]);
        },

        animatetoggle(trigger) {
            animateToggle(this.$refs[trigger]);
        },

        openModal(id: string) {
            MicroModal.show(id, { disableFocus: true, disableScroll: true });
        },

        closeModal(id: string) {
            MicroModal.close(id);
        },

        // Downloading
        download(serviceByDateResponse: IReportServiceByDateResponce, DownloadFileType: string, reportType: string, summary?) {
            const services = serviceByDateResponse?.services ?? [];
            let mappedServices = [];
            if (reportType === 'detailed-service') {
                mappedServices = services.map((service) => ({
                    [i18n.t('reports.table.invoice_no').toString()]:
                        service.documents.find((document) => document.documentType === 'INVOICE') === undefined
                            ? null
                            : service.documents.find((document) => document.documentType === 'INVOICE').documentNo,
                    [i18n.t('reports.table.service_start_date').toString()]: this.formatDate(service.servicestartdate),
                    [i18n.t('reports.table.service_end_date').toString()]: this.formatDate(service.serviceenddate),
                    [i18n.t('reports.table.nights').toString()]: this.calculateNights(service.servicestartdate, service.serviceenddate),
                    [i18n.t('reports.table.all_departures').toString()]: [
                        service.servicedeparturecountry,
                        service.servicedeparturecity
                    ].join(', '),
                    [i18n.t('reports.table.all_destinations').toString()]: [
                        service.servicedestinationcountry,
                        service.servicedestinationcity
                    ].join(', '),
                    [i18n.t('reports.table.passenger_name').toString()]: service.servicepassengerlist
                        .map((passenger) => passenger.name)
                        .join(', '),
                    [i18n.t('reports.table.all_services').toString()]: service.productname,
                    [i18n.t('reports.table.qty').toString()]: service.qty,
                    [i18n.t('reports.table.invoice_sum').toString()]: service.ttlsellrate,
                    [i18n.t('reports.table.all_service_providers').toString()]: service.serviceprovider,
                    [i18n.t('reports.table.consultant').toString()]: service.travelconsultant,
                    [i18n.t('reports.table.booked_by').toString()]: service.bookedbyname,
                    [i18n.t('reports.table.sales_channel').toString()]: service.saleschannels
                }));
            } else if (reportType === 'summary-service' && summary) {
                mappedServices = Array.from(summary.entries()).map((entry) => ({
                    [i18n.t('reports.table.service_groups').toString()]: entry[0],
                    [i18n.t('reports.table.invoice_sum').toString()]: entry[1]
                }));
            } else if (reportType === 'detailed-passenger') {
                mappedServices = services.map((service) => ({
                    [i18n.t('reports.table.passenger_name').toString()]: service.servicepassengerlist
                        .map((passenger) => passenger.name)
                        .join(', '),
                    [i18n.t('reports.table.passenger_ref_no').toString()]: service.servicepassengerlist
                        .map((passenger) => passenger.reference)
                        .filter(Boolean)
                        .join(', '),
                    [i18n.t('reports.table.invoice_no').toString()]:
                        service.documents.find((document) => document.documentType === 'INVOICE') === undefined
                            ? null
                            : service.documents.find((document) => document.documentType === 'INVOICE').documentNo,
                    [i18n.t('reports.table.invoice_sum').toString()]: service.ttlsellrate,
                    [i18n.t('reports.table.booked_by').toString()]: service.bookedbyname
                }));
            } else if (reportType === 'summary-passenger' && summary) {
                mappedServices = Array.from(summary.entries()).map((entry) => ({
                    [i18n.t('reports.table.passenger_name').toString()]: entry[0],
                    [i18n.t('reports.table.invoice_sum').toString()]: entry[1]
                }));
            } else if (reportType === 'travel') {
                mappedServices = services.map((service) => ({
                    [i18n.t('reports.table.service_start_date').toString()]: this.formatDate(service.servicestartdate),
                    [i18n.t('reports.table.service_end_date').toString()]: this.formatDate(service.serviceenddate),
                    [i18n.t('reports.table.passenger_name').toString()]: service.servicepassengerlist
                        .map((passenger) => passenger.name)
                        .join(', '),
                    [i18n.t('reports.table.all_destinations').toString()]: service.servicedestinationcountry,
                    [i18n.t('reports.table.all_services').toString()]: service.productname,
                    [i18n.t('reports.table.documents').toString()]: service.documents
                        .map((doc) => doc.documentNo)
                        .filter(Boolean)
                        .join(', ')
                }));
            }
            const data = XLSX.utils.json_to_sheet(mappedServices);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, data, 'data');
            if (DownloadFileType === '.xlsx') {
                XLSX.writeFile(wb, reportType + '-report.xlsx');
            } else if (DownloadFileType === '.csv') {
                XLSX.writeFile(wb, reportType + '-report.csv', { bookType: 'csv' });
            }
        },
        downloadInvoice(documentId: string, label: string) {
            const url = '/api/invoices/' + documentId + '?customerid=' + store.state.customerId;
            const encodedToken = store.state.token;
            Axios.get(url, {
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken },
                responseType: 'blob'
            })
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = label;
                    link.click();
                    URL.revokeObjectURL(link.href);
                })
                .catch(console.error);
        },
        downloadDocument(documentId: string, documentType: string, label: string) {
            let type = '';
            switch (documentType) {
                case 'ITINERARY':
                    type = 'itineraries';
                    break;
                case 'VOUCHER':
                    type = 'vouchers';
                    break;
                case 'FILE':
                    type = 'files';
                    break;
                case 'AIR_TICKET':
                    type = 'tickets';
                    break;
                case 'INVOICE':
                    type = 'invoices';
                    break;
                default:
                    type = 'files';
            }
            let url = '';
            if (type === 'invoices') {
                url = '/api/invoices/' + documentId;
            } else {
                url = '/api/documents/' + type + '/' + documentId;
            }
            url += '?customerid=' + store.state.customerId;
            const encodedToken = store.state.token;
            Axios.get(url, {
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken },
                responseType: 'blob'
            })
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = label;
                    link.click();
                    URL.revokeObjectURL(link.href);
                })
                .catch(console.error);
        },

        // Scrolling functions

        scrollToResultsTable(): void {
            const resultsTableElement = document.getElementById('resultsTable');
            const scrollOptions: any = {
                behavior: 'smooth',
                block: 'start'
            };
            scrollOptions.top = Math.max(resultsTableElement.getBoundingClientRect().top + window.scrollY - 60, 0);
            window.scrollTo(scrollOptions);
        },

        scrollToAlertMessage(): void {
            const errorMessageElement = document.getElementsByClassName('title-2xl')[0];
            // errorMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const scrollOptions: any = {
                behavior: 'smooth',
                block: 'start'
            };
            // Ensure the top offset is not negative to avoid scrolling too far up
            // Calculate the adjusted top offset by subtracting navbar height
            scrollOptions.top = Math.max(errorMessageElement.getBoundingClientRect().top + window.scrollY - 100, 0);
            // Scroll to the adjusted position
            window.scrollTo(scrollOptions);
        },

        // Report's description expansion
        initDescriptionExpansion(): void {
            Vue.nextTick(() => {
                const showFullDescLink = this.$refs.showFullDesc as HTMLElement;
                if (this.showFullDesc === true) {
                    showFullDescLink.style.display = 'none';
                }
                if (showFullDescLink instanceof HTMLElement) {
                    showFullDescLink.addEventListener('click', (event) => {
                        event.preventDefault();
                        this.showFullDesc = true;
                        showFullDescLink.style.display = 'none';
                    });
                    showFullDescLink.addEventListener('mouseenter', () => {
                        showFullDescLink.style.color = 'orange'; // Change color on hover
                    });
                    showFullDescLink.addEventListener('mouseleave', () => {
                        showFullDescLink.style.color = ''; // Change color back on mouse leave
                    });
                }
            });
        }
    }
};
